/**
 * Compiles the quadratic-core wasm exactly once on the main thread. The
 * resulting `WebAssembly.Module` is shared with the web workers via
 * `postMessage` so every thread instantiates the same compiled code instead of
 * each fetching and compiling its own (~26 MB raw) copy. Engines share the
 * compiled machine code across instantiations of a single `Module`, so this
 * removes the duplicated wasm code memory on the light threads.
 *
 * `WebAssembly.Module` is structured-clone serializable (not transferable), so
 * it is passed in the message body and must not be placed in a transfer list.
 */

import wasmUrl from '@/app/quadratic-core/quadratic_core_bg.wasm?url';

let modulePromise: Promise<WebAssembly.Module> | null = null;

export const compileWasmModule = (): Promise<WebAssembly.Module> => {
  if (!modulePromise) {
    modulePromise = compile();
  }
  return modulePromise;
};

const compile = async (): Promise<WebAssembly.Module> => {
  try {
    // `compileStreaming` requires the server to respond with `application/wasm`.
    // Fall back to a buffered compile when it is unavailable or the mime type is
    // wrong (e.g. some dev/proxy setups).
    if (typeof WebAssembly.compileStreaming === 'function') {
      try {
        return await WebAssembly.compileStreaming(fetch(wasmUrl));
      } catch (e) {
        // compileStreaming throws when the response mime type isn't
        // `application/wasm`, but also for network errors, CSP violations, or a
        // corrupt module. Log so those non-mime failures are debuggable, then
        // fall through to the buffered compile.
        console.warn('[compileWasmModule] compileStreaming failed, falling back to buffered compile:', e);
      }
    }
    const response = await fetch(wasmUrl);
    const bytes = await response.arrayBuffer();
    return WebAssembly.compile(bytes);
  } catch (e) {
    // Clear the cached promise so a later call can retry instead of forever
    // returning this rejected promise (e.g. a transient network failure).
    modulePromise = null;
    throw e;
  }
};
